// ** React Imports
import { useState, useEffect, useMemo, useRef, useContext } from "react";

// ** Custom Components
import Block from "components/layout/Block";
import Text from "components/typography/Text";
import CarouselSwiperNew from "./CarouselSwiperNew";
import SliderPagination from "./SliderPagination";
import InteractionsInModal from "components/user/Interactions/InteractionsInModal";
import ThumbnailsSwiperNew from "./ThumbnailsSwiperNew";
// ** Custom Hooks
import useModal from "hooks/useModal";
import { useResizeObserver } from "hooks/useResizeObserver";

// ** Utils & Helpers
import pseudoFormat from "utils/pseudoFormat";

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";

// ** Styled Components
import { StyledWrapper, StyledContentWrapper } from "./styles";

// #####################################################

const THUMBNAIL_SIZE = 90;
const SPACE_BETWEEN = 3;

const SwipersWrapperNew = ({
	carouselSwiper,
	setCarouselSwiper,
	mediaType,
	mediaData,
	mediaMeta,
	setShowComments,
	contentInfo,
	iCommented,
	disabledAddComment,
	countComments,
	onCommentClick,
	contentIdUrl,
	showCommentsSection,
	setShowCommentsSection,
	setVisibleEditor,
}) => {
	const { isPhone, isTablet } = useContext(deviceTypeContext);

	// ** Referencja do wrappera - 'contentWrapper'
	const contentWrapperRef = useRef(null);

	const [thumbnailSwiper, setThumbnailSwiper] = useState(null);

	// ** Zmienne stanu komponentu
	const [activeIndex, setActiveIndex] = useState(0);
	const [activeSlideData, setActiveSlideData] = useState(null);
	const [firstFilledSlideIndex, setFirstFilledSlideIndex] = useState(null);
	const [firstSlideAfterInit, setFirstSlideAfterInit] = useState(false);
	const [showSwipers, setShowSwipers] = useState(false);
	const [contentInfoLoading, setContentInfoLoading] = useState(
		contentInfo.loading
	);
	const [thumbnailsPerView, setThumbnailsPerView] = useState(5);

	// ** useModal Hook
	const { parsedLink } = useModal();

	// ** useMemo Hook - filtruje listę mediów użytkownika, na podstawie parametru "_filled",
	// ** i tworzy listę wirtualnych slajdów, która jest aktualizowana tylko w przypadku
	// ** zmiany ilości slajdów pozostałych z lewej "left_amount" oraz prawej strony "right_amount"
	const virtualSlides = useMemo(() => {
		const filledSlides = mediaData.filter(
			(slide) => slide._filled === true
		);
		const lastFilledSlideIndex =
			filledSlides[filledSlides.length - 1]?._innerIndex;
		setFirstFilledSlideIndex(filledSlides[0]?._innerIndex);
		return mediaData.slice(0, lastFilledSlideIndex + 1);
	}, [mediaData?.length, mediaMeta?.left_amount, mediaMeta?.right_amount]);

	// ** Efekt odpowiadajacy za przejście do odpowiedniego
	// ** zdjęcia / filmu po otwarciu modala
	useEffect(() => {
		if (mediaData.length) {
			const centerItemIndex = virtualSlides?.findIndex(
				(item) => item.index === 0
			);
			carouselSwiper?.slideTo(centerItemIndex);
			thumbnailSwiper?.slideTo(centerItemIndex);
			setFirstSlideAfterInit(true);
			setActiveIndex(centerItemIndex);
			setActiveSlideData(virtualSlides[centerItemIndex]);
		}
	}, [mediaData.length]);

	// ** Efekt ustawiający parametr 'showSwipers' po załadowaniu slajdów,
	// ** i pierwszym przejściu do odpowiedniego slajdu (za pomocą efektu powyżej),
	useEffect(() => {
		if (firstSlideAfterInit) {
			setTimeout(() => {
				setShowSwipers(true);
				setFirstSlideAfterInit(false);
			}, 250);
		}
	}, [firstSlideAfterInit]);

	useEffect(() => {
		if (contentInfo.loading) {
			setContentInfoLoading(true);
		} else {
			setContentInfoLoading(false);
		}
	}, [contentInfo.loading]);

	// #####################################################

	// ** useResizeObserver Hook
	// ** - obliczanie ilości slajdów w ThumbnailSwiper
	useResizeObserver(contentWrapperRef, (entry) => {
		const thumbnailsCount =
			(entry.width + SPACE_BETWEEN) / (THUMBNAIL_SIZE + SPACE_BETWEEN);
		setThumbnailsPerView(thumbnailsCount);
	});

	return (
		<Block relative flex column justifyCenter alignCenter fullHeight>
			<StyledWrapper>
				<StyledContentWrapper ref={contentWrapperRef}>
					<CarouselSwiperNew
						carouselSwiper={carouselSwiper}
						setCarouselSwiper={setCarouselSwiper}
						thumbnailSwiper={thumbnailSwiper}
						setShowComments={setShowComments}
						setActiveIndex={setActiveIndex}
						firstFilledSlideIndex={firstFilledSlideIndex}
						virtualSlides={virtualSlides}
						mediaMeta={mediaMeta}
						parsedLink={parsedLink}
						showSwipers={showSwipers}
						activeSlideData={activeSlideData}
						setActiveSlideData={setActiveSlideData}
						contentInfo={contentInfo}
						contentIdUrl={contentIdUrl}
						iCommented={iCommented}
						disabledAddComment={disabledAddComment}
						countComments={countComments}
						onCommentClick={onCommentClick}
						contentInfoLoading={contentInfoLoading}
						showCommentsSection={showCommentsSection}
						setShowCommentsSection={setShowCommentsSection}
						setVisibleEditor={setVisibleEditor}
					/>
					{isPhone || isTablet ? (
						<>
							<Block
								flex
								fullWidth
								pl={4}
								style={{
									height: "35px",
									overflow: "hidden",
									marginTop: "5px",
								}}
								alignCenter
							>
								{!contentInfoLoading && (
									<InteractionsInModal
										userId={contentInfo?.data?.userId}
										id={contentInfo?.data?.contentId}
										type={mediaType}
										canLike={contentInfo?.data?.canLike}
										likes={contentInfo?.data?.countLike}
										isLiked={contentInfo?.data?.iLiked}
										comments={countComments}
										isCommented={iCommented}
										onCommentClick={onCommentClick}
										isPhone={isPhone}
										isTablet={isTablet}
										alwaysDark
										showCommentsSection={
											showCommentsSection
										}
									/>
								)}
								<Text
									style={{
										position: "absolute",
										right: "1rem",
									}}
									color="whiteAlways"
									small
								>
									{pseudoFormat(activeIndex + 1)}
									<Text ml={1} mr={1}>
										/
									</Text>
									{pseudoFormat(mediaData.length)}
								</Text>
							</Block>
							<Block
								relative
								flex
								justifyCenter
								alignCenter
								width="100%"
								style={{ height: "11.2px" }}
								mt={3}
								mb={3}
							>
								<SliderPagination
									activeSlide={activeIndex}
									totalSlides={virtualSlides.length}
									onPaginationClick={(i) => {
										setActiveIndex(i);
										carouselSwiper.slideTo(i);
									}}
								/>
							</Block>
						</>
					) : (
						<Block fullWidth height="90px" mt={2} mb={1}>
							<ThumbnailsSwiperNew
								carouselSwiper={carouselSwiper}
								thumbnailSwiper={thumbnailSwiper}
								setThumbnailSwiper={setThumbnailSwiper}
								activeIndex={activeIndex}
								firstFilledSlideIndex={firstFilledSlideIndex}
								virtualSlides={virtualSlides}
								thumbnailsPerView={thumbnailsPerView}
								spaceBetween={SPACE_BETWEEN}
								mediaMeta={mediaMeta}
								parsedLink={parsedLink}
								showSwipers={showSwipers}
							/>
						</Block>
					)}
				</StyledContentWrapper>
			</StyledWrapper>
		</Block>
	);
};

export default SwipersWrapperNew;
